import { useState } from 'react'

import {
  traceService,
  type InvariantPromotionEvidence,
  type InvariantSample,
  type InvariantVerdict,
} from '../services/traceService'

type LoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; samples: InvariantSample[]; promotion: InvariantPromotionEvidence | null }

const VERDICT_LABEL: Record<InvariantVerdict, string> = {
  true_positive: 'Verdadero positivo',
  false_positive: 'Falso positivo',
}

const PANEL_STYLE = {
  marginTop: 8,
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid rgba(148,163,184,0.25)',
  background: 'rgba(148,163,184,0.06)',
  fontSize: 12,
} as const

const BUTTON_STYLE = {
  padding: '4px 8px',
  borderRadius: 8,
  border: '1px solid rgba(148,163,184,0.32)',
  background: 'rgba(148,163,184,0.12)',
  color: '#e2e8f0',
  fontSize: 11,
  fontWeight: 700,
  cursor: 'pointer',
} as const

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'No se pudo completar la solicitud'
}

function formatRate(rate: number | null): string {
  return rate === null ? 'sin datos' : `${(rate * 100).toFixed(1)}%`
}

type Props = {
  tenantId: string
  alertId: string
  invariantId: string
}

/**
 * Muestras de un invariante en warn con botones de veredicto. Sin veredictos humanos no se puede medir el
 * FPR que exige el protocolo para promover un invariante a rewrite.
 */
export default function InvariantSamplesPanel({ tenantId, alertId, invariantId }: Props) {
  const [state, setState] = useState<LoadState>({ status: 'idle' })
  const [pendingTraceId, setPendingTraceId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const load = async () => {
    setState({ status: 'loading' })
    try {
      const [samples, promotion] = await Promise.all([
        traceService.getInvariantSamples({ tenantId, invariantId, limit: 20 }),
        traceService.getInvariantPromotion({ tenantId, invariantId }).catch(() => null),
      ])
      setState({ status: 'ready', samples: samples.data, promotion: promotion?.data ?? null })
    } catch (error) {
      setState({ status: 'error', message: errorMessage(error) })
    }
  }

  const label = async (traceId: string, verdict: InvariantVerdict) => {
    setPendingTraceId(traceId)
    setActionError(null)
    try {
      await traceService.recordInvariantVerdict({ tenantId, alertId, traceId, invariantId, verdict })
      const promotion = await traceService.getInvariantPromotion({ tenantId, invariantId }).catch(() => null)
      setState((current) =>
        current.status === 'ready'
          ? {
              status: 'ready',
              promotion: promotion?.data ?? current.promotion,
              samples: current.samples.map((sample) => (sample.traceId === traceId ? { ...sample, verdict } : sample)),
            }
          : current
      )
    } catch (error) {
      setActionError(errorMessage(error))
    } finally {
      setPendingTraceId(null)
    }
  }

  return (
    <div style={PANEL_STYLE}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 800, color: '#e2e8f0' }}>{invariantId}</span>
        <button type="button" style={BUTTON_STYLE} onClick={load} disabled={state.status === 'loading'}>
          {state.status === 'ready' ? 'Recargar muestras' : 'Ver muestras para etiquetar'}
        </button>
      </div>

      {state.status === 'loading' ? <div role="status">Cargando muestras…</div> : null}

      {state.status === 'error' ? (
        <div role="alert" style={{ color: '#fca5a5', marginTop: 6 }}>
          {state.message}{' '}
          <button type="button" style={BUTTON_STYLE} onClick={load}>
            Reintentar
          </button>
        </div>
      ) : null}

      {state.status === 'ready' ? (
        <div style={{ marginTop: 6 }}>
          {state.promotion ? (
            <div style={{ marginBottom: 6, color: 'rgba(226,232,240,0.8)' }}>
              Etiquetadas: {state.promotion.labelled} · Falsos positivos: {state.promotion.falsePositives} · FPR:{' '}
              {formatRate(state.promotion.falsePositiveRate)} ·{' '}
              <strong>{state.promotion.canPromote ? 'Evidencia suficiente para promover' : 'Aún no promovible'}</strong>
              {state.promotion.blockers.length > 0 ? (
                <ul style={{ margin: '4px 0 0 16px' }}>
                  {state.promotion.blockers.map((blocker) => (
                    <li key={blocker}>{blocker}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          {actionError ? (
            <div role="alert" style={{ color: '#fca5a5', marginBottom: 6 }}>
              {actionError}
            </div>
          ) : null}

          {state.samples.length === 0 ? (
            <div>No hay muestras recientes de {invariantId}. Nada que etiquetar por ahora.</div>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }}>
              {state.samples.map((sample) => (
                <li key={sample.traceId} style={{ borderTop: '1px solid rgba(148,163,184,0.18)', paddingTop: 6 }}>
                  <div style={{ color: 'rgba(226,232,240,0.85)' }}>{sample.message ?? '(sin mensaje)'}</div>
                  <div style={{ fontSize: 11, color: 'rgba(226,232,240,0.5)', fontFamily: 'ui-monospace, monospace' }}>
                    {sample.traceId}
                    {sample.details ? ` · ${JSON.stringify(sample.details)}` : ''}
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                    {(Object.keys(VERDICT_LABEL) as InvariantVerdict[]).map((verdict) => (
                      <button
                        key={verdict}
                        type="button"
                        style={{
                          ...BUTTON_STYLE,
                          ...(sample.verdict === verdict ? { background: 'rgba(34,197,94,0.22)', color: '#bbf7d0' } : {}),
                        }}
                        aria-pressed={sample.verdict === verdict}
                        disabled={pendingTraceId === sample.traceId}
                        onClick={() => label(sample.traceId, verdict)}
                      >
                        {VERDICT_LABEL[verdict]}
                      </button>
                    ))}
                    {sample.verdict ? <span>Etiqueta: {VERDICT_LABEL[sample.verdict]}</span> : <span>Sin etiquetar</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  )
}
